import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { SettingsLayout } from './SettingsLayout';
import { useReferralPartner, useReferralStats } from '../../hooks/useReferral';
import { useSubscription } from '../../hooks/useSubscription';
import { Separator } from '../../components/ui/separator';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Copy, Check, X, Link, TrendingUp, Sparkles, UserPlus, Clock } from 'lucide-react';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { getPlanBadgeStyle } from '../../lib/usage';
import { GradientBorder } from '../../components/ui/gradient-border';
import { PulsingDot } from '../../components/ui/pulsing-dot';

export function SettingsReferralPage() {
  const { data: partner, isLoading: partnerLoading } = useReferralPartner();
  const { data: statsData, isLoading: statsLoading } = useReferralStats();
  const { basePlanCredits, referralBonusCredits, periodAllowance } = useCreditBalance();
  const { plans } = useSubscription();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  // Redirect to account page if partner has no active paid subscription
  useEffect(() => {
    if (!partnerLoading && partner?.isPartner && !partner.hasActiveSubscription) {
      navigate({ to: '/settings/account' });
    }
  }, [partnerLoading, partner, navigate]);

  if (partnerLoading) {
    return (
      <SettingsLayout activeTab="referrals">
        <div className="flex items-center justify-center py-16">
          <PulsingDot />
        </div>
      </SettingsLayout>
    );
  }

  if (!partner?.isPartner || !partner.hasActiveSubscription) {
    return null;
  }

  const handleCopy = async () => {
    if (!partner.link) return;
    try {
      await navigator.clipboard.writeText(partner.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  const stats = statsData?.stats;
  const referrals = statsData?.referrals ?? [];
  const commissionRate = partner.commissionRate ?? 0.075;
  const months = partner.commissionMonths ?? 12;

  const paidPlans = plans.filter((p) => p.price > 0);
  const exampleBonuses = paidPlans.map((p) => ({
    name: p.name,
    price: p.price,
    // Mirror the server's calculateCredits rounding (ceil to nearest 25) so this
    // estimate matches the bonus actually granted at activation.
    bonus: Math.ceil((commissionRate * p.price * 100) / 25) * 25,
  }));

  return (
    <SettingsLayout activeTab="referrals">
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Referrals</h1>
          <p className="text-sm text-white/50 mt-1">
            For every subscriber you refer, your monthly credits
            increase. This bonus continues for up to {months} months per
            referral. Plus, anyone who subscribes through your link gets{' '}
            {(partner.referredFlatCredits ?? 1000).toLocaleString()} bonus credits.
          </p>
          {exampleBonuses.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-white/40 mb-2">Your plan increase per referral:</p>
            <div className="grid grid-cols-3 gap-3">
              {exampleBonuses.map((ex) => (
                <div
                  key={ex.name}
                  className="rounded-lg border border-white/10 px-4 py-3"
                >
                  <div className="mb-1">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider ${getPlanBadgeStyle(ex.name.toLowerCase())}`}
                      style={{ fontSize: '12px' }}
                    >
                      {ex.name}
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-green-400">
                    +{ex.bonus.toLocaleString()} credits/mo
                  </p>
                </div>
              ))}
            </div>
            </div>
          )}
        </div>

        <Separator className="bg-white/10" />

        {/* Referral Link */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-1">
            Your Referral Link
          </h2>
          <p className="text-sm text-white/50 mb-4">
            Share this link. When they subscribe, they get{' '}
            {(partner.referredFlatCredits ?? 1000).toLocaleString()} bonus
            credits and you get a monthly credit boost.
          </p>
          <GradientBorder>
            <div className="flex items-center gap-3 p-4">
              <Link className="h-4 w-4 text-white/40 shrink-0" />
              <code className="flex-1 text-sm text-white/80 truncate">
                {partner.link}
              </code>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 text-sm text-white/80 transition-colors shrink-0"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </GradientBorder>
        </section>

        <Separator className="bg-white/10" />

        {/* Referrals */}
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-white">Your Referrals</h2>

          {statsLoading ? (
            <div className="flex items-center justify-center py-8">
              <PulsingDot />
            </div>
          ) : stats ? (
            <>
              {/* Stat Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard
                  icon={UserPlus}
                  label="Signups"
                  value={stats.pendingReferrals}
                />
                <StatCard
                  icon={TrendingUp}
                  label="Paid"
                  value={stats.activeReferrals}
                />
                <StatCard
                  icon={Sparkles}
                  label="Monthly Bonus"
                  value={`+${stats.monthlyBonusCredits.toLocaleString()}`}
                />
                {referralBonusCredits > 0 ? (
                  <GradientBorder>
                    <div className="p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Base Plan</span>
                        <span className="text-white/80">{basePlanCredits.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Referral Bonus</span>
                        <span className="text-green-400">+{referralBonusCredits.toLocaleString()}</span>
                      </div>
                      <Separator className="bg-white/10" />
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-white/70">Monthly Total</span>
                        <span className="text-white">{periodAllowance.toLocaleString()}</span>
                      </div>
                    </div>
                  </GradientBorder>
                ) : (
                  <StatCard
                    icon={Sparkles}
                    label="Monthly Credits"
                    value={basePlanCredits.toLocaleString()}
                  />
                )}
              </div>
            </>
          ) : null}
          {referrals.length === 0 ? (
            <p className="text-white/40 text-sm">
              No referrals yet. Share your link to start tracking signups.
              Your monthly credits increase when referrals subscribe.
            </p>
          ) : (
            <GradientBorder>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-white/60">User</TableHead>
                    <TableHead className="text-white/60">Plan</TableHead>
                    <TableHead className="text-white/60">Status</TableHead>
                    <TableHead className="text-white/60 text-right">
                      Monthly Bonus
                    </TableHead>
                    <TableHead className="text-white/60 text-right">
                      Months Left
                    </TableHead>
                    <TableHead className="text-white/60 text-right">
                      Signed Up
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.map((ref) => (
                    <TableRow key={ref.id} className="border-white/10">
                      <TableCell className="text-white/80 font-mono text-sm">
                        {ref.referredEmail}
                      </TableCell>
                      <TableCell>
                        {ref.planName ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider ${getPlanBadgeStyle(ref.planName.toLowerCase())}`}
                            style={{ fontSize: '12px' }}
                          >
                            {ref.planName}
                          </span>
                        ) : ref.status === 'pending' ? (
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider text-white/40 bg-white/5 border border-white/10"
                            style={{ fontSize: '12px' }}
                          >
                            Free
                          </span>
                        ) : (
                          <span className="text-white/30">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {ref.status === 'active' ? (
                          <span className="inline-flex items-center gap-1 text-sm text-green-400">
                            <Check className="h-3.5 w-3.5" />
                            Paid
                          </span>
                        ) : ref.status === 'pending' ? (
                          <span className="inline-flex items-center gap-1 text-sm text-amber-400">
                            <Clock className="h-3.5 w-3.5" />
                            Free
                          </span>
                        ) : ref.status === 'churned' || ref.status === 'expired' ? (
                          <span className="inline-flex items-center gap-1 text-sm text-red-400">
                            <X className="h-3.5 w-3.5" />
                            Cancelled
                          </span>
                        ) : (
                          <span className="text-sm text-white/40 capitalize">
                            {ref.status}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-white/80">
                        {ref.status === 'active' ? `+${ref.monthlyBonus.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {ref.monthsRemaining !== null ? (
                          <span
                            className={
                              ref.monthsRemaining <= 3
                                ? 'text-amber-400'
                                : 'text-white/60'
                            }
                          >
                            {ref.monthsRemaining}
                          </span>
                        ) : (
                          <span className="text-white/30">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-white/60 text-sm">
                        {new Date(ref.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GradientBorder>
          )}
        </div>
      </div>
    </SettingsLayout>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string | number;
}) {
  return (
    <GradientBorder className="h-full" innerClassName="h-full">
      <div className="p-4 h-full flex flex-col justify-between">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="h-4 w-4 text-white/40" />
          <span className="text-sm text-white/50">{label}</span>
        </div>
        <p className="text-2xl font-semibold text-white">{value}</p>
      </div>
    </GradientBorder>
  );
}
